// Detect whether a single WheelEvent looks like it came from a trackpad rather
// than a clicky mouse wheel. Mouse wheels emit discrete, integer, single-axis
// events. Trackpads emit small, often fractional, two-axis events.
//
// This per-event test is only a hint. Windows precision touchpads in Chrome
// emit integer, single-axis deltas (25 / 50 / 75 / 100 …) that are
// indistinguishable from mouse notches one event at a time, so the gate below
// classifies by the *stream* (event cadence) rather than trusting this alone.
export function isTrackpadEvent(e) {
  if (e.deltaMode === 1) return false;
  if (e.deltaX !== 0 && e.deltaY !== 0) return true;
  if (e.deltaY % 1 !== 0) return true;
  if (e.deltaMode === 0 && Math.abs(e.deltaY) < 50) return true;
  return false;
}

// One-action-per-gesture wheel gate.
//
// Browsers give no "gesture started / inertia" phase on wheel events, so this
// uses the same intent signal the established full-page libraries settled on
// (fullPage.js, Swiper's mousewheel module, Lethargy): a wheel event is a NEW
// intention only when the stream is ACCELERATING — its magnitude climbs above
// what came just before — or the direction flips, or the input has been quiet
// for a long time. Inertia only ever decays, so decaying or flat streams are
// never intent, however long they run. Swiper additionally ignores anything
// within 500ms of a snap that does not accelerate; fullPage.js blocks for the
// animation length and then requires acceleration. Both ideas are here.
//
// Rules, in order:
//   1. Burst = events closer together than silenceMs. First event of a burst
//      is accepted outright unless rule 3 applies.
//   2. While a burst is live the gate is locked. A restart inside the burst is
//      allowed only after lockMs (the slide animation) AND on a sustained
//      ramp — the last `rampEvents` deltas each ≥ the previous, the newest ≥
//      restartMinDelta, and the newest window averaging > accelRatio × the
//      window before it. The ramp is measured in VELOCITY (delta per ms of
//      gap), not raw delta: when the main thread stalls (e.g. the backdrop
//      cross-fade between CATALOG and BRANDS) Chrome merges several OS
//      events into one bigger delta spanning a bigger gap — same velocity,
//      so it never reads as acceleration.
//   3. A burst that starts after silence but within resumeMs of the previous
//      burst, in the SAME direction, is treated as that gesture's inertia
//      resuming (Windows can pause between the finger phase and the fling)
//      unless it proves intent: it ramps as in rule 2, or it opens far above
//      where the previous burst ended (resumeJumpRatio × last delta).
//   4. A direction flip with a meaningful delta is always intent.
//   5. Mouse notches (integer, ≥ 50, spaced ≥ rapidGapMs apart) keep their
//      own light gate: release on mouseSilenceMs quiet or mouseReleaseMs.
//
// Debug: set window.__wheelDebug = 1 in the console to log every decision.
// Returns { accept, reset }: call accept(e) per wheel event (true iff the
// event should trigger an action). Call reset() on unmount.
export function createWheelGate({
  trackpadLockMs = 350,        // min lock inside a burst (slide animation)
  trackpadSilenceMs = 200,     // burst ends after this much quiet
  rapidGapMs = 40,             // events closer than this = trackpad burst
  resumeMs = 700,              // same-direction burst within this = suspect
  resumeJumpRatio = 3,         // …unless it opens this far above the last delta
  restartMinDelta = 10,        // a restart / resume needs at least this delta
  rampEvents = 3,              // consecutive non-decreasing deltas = ramp
  accelRatio = 1.5,            // newest window avg vs previous window avg
  mouseSilenceMs = 60,
  mouseReleaseMs = 370,
} = {}) {
  let locked = false;
  let lockedAt = 0;
  let silenceTimer = null;
  let releaseTimer = null;

  let lastEventAt = 0;
  let lastAbs = 0;
  let lastSign = 0;
  let burstTrackpad = false;
  let history = [];            // velocities in the current burst (newest last)
  let prevBurstLastAbs = 0;    // where the previous burst ended
  let prevBurstLastV = 0;
  let lastV = 0;
  let prevBurstSign = 0;
  let prevBurstEndedAt = 0;
  let resumeSuspect = false;   // rule 3 applies to this burst

  const debug = (...args) => {
    if (typeof window !== "undefined" && window.__wheelDebug) {
      // eslint-disable-next-line no-console
      console.log("[wheelGate]", ...args);
    }
  };

  const clearTimers = () => {
    clearTimeout(silenceTimer);
    clearTimeout(releaseTimer);
    silenceTimer = null;
    releaseTimer = null;
  };

  const endBurst = () => {
    prevBurstLastAbs = lastAbs;
    prevBurstLastV = lastV;
    prevBurstSign = lastSign;
    prevBurstEndedAt = lastEventAt;
    history = [];
  };

  const unlock = () => {
    locked = false;
    burstTrackpad = false;
    endBurst();
    debug("unlock");
  };

  // Sustained acceleration: the newest `rampEvents` deltas are non-decreasing,
  // the newest is meaningful, and the newest window averages well above the
  // window before it. Needs at least rampEvents + 1 samples.
  const isRamping = () => {
    const n = history.length;
    if (n < rampEvents + 1) return false;
    const recent = history.slice(n - rampEvents);
    for (let i = 1; i < recent.length; i++) {
      if (recent[i] < recent[i - 1]) return false;
    }
    if (lastAbs < restartMinDelta) return false;
    const before = history.slice(Math.max(0, n - rampEvents * 2), n - rampEvents);
    const avg = (a) => a.reduce((s, v) => s + v, 0) / a.length;
    return avg(recent) > avg(before) * accelRatio;
  };

  const acceptNow = (e, why, extra) => {
    locked = true;
    lockedAt = performance.now();
    resumeSuspect = false;
    history = [lastV];
    debug("ACCEPT " + why, { dy: e.deltaY, ...extra });
    return true;
  };

  const accept = (e) => {
    const now = performance.now();
    const abs = Math.abs(e.deltaY);
    const sign = Math.sign(e.deltaY);
    const gap = lastEventAt ? now - lastEventAt : Infinity;
    const prevSign = lastSign;
    lastEventAt = now;

    if (abs === 0) return false;

    // Burst boundary: a quiet gap. (The silence timer normally fires first and
    // calls unlock → endBurst; this covers the case where it hasn't yet.)
    const newBurst = gap > trackpadSilenceMs;
    if (newBurst) {
      if (history.length) endBurst();
      burstTrackpad = false;
      const sinceLastBurst = prevBurstEndedAt ? now - prevBurstEndedAt : Infinity;
      resumeSuspect = sinceLastBurst < resumeMs && sign === prevBurstSign;
    }

    if (isTrackpadEvent(e) || gap < rapidGapMs) {
      if (!burstTrackpad) {
        burstTrackpad = true;
        clearTimeout(releaseTimer);
        releaseTimer = null;
      }
    }

    // Velocity: delta per ms of the gap it covers. The first event of a burst
    // has no meaningful gap; treat it as one frame.
    const frameGap = gap > trackpadSilenceMs ? 16 : Math.max(gap, 4);
    const v = abs / frameGap;
    lastAbs = abs;
    lastV = v;
    lastSign = sign;

    if (burstTrackpad) {
      clearTimeout(silenceTimer);
      silenceTimer = setTimeout(unlock, trackpadSilenceMs);
      history.push(v);
      if (history.length > rampEvents * 2) history.shift();

      const flipped = prevSign !== 0 && sign !== prevSign && abs >= restartMinDelta;

      if (locked) {
        if (now - lockedAt < trackpadLockMs) {
          debug("hold (animating)", { dy: e.deltaY, gap: Math.round(gap) });
          return false;
        }
        if (flipped) return acceptNow(e, "flip", { gap: Math.round(gap) });
        if (isRamping()) return acceptNow(e, "restart (ramp)", { history: [...history] });
        debug("hold (inertia)", { dy: e.deltaY, gap: Math.round(gap), v: +v.toFixed(2) });
        return false;
      }

      // Unlocked: a burst that just started.
      if (resumeSuspect) {
        // Same direction, shortly after the last gesture: prove it's new.
        if (flipped) return acceptNow(e, "flip", {});
        if (abs >= restartMinDelta && v >= prevBurstLastV * resumeJumpRatio) {
          return acceptNow(e, "resume-jump", { prevEndV: +prevBurstLastV.toFixed(2), v: +v.toFixed(2) });
        }
        if (isRamping()) return acceptNow(e, "resume (ramp)", { history: [...history] });
        debug("hold (resume-suspect)", { dy: e.deltaY, gap: Math.round(gap), prevEnd: prevBurstLastAbs });
        return false;
      }
      return acceptNow(e, "burst-start", { gap: Math.round(gap) });
    }

    // Mouse path.
    clearTimeout(silenceTimer);
    silenceTimer = setTimeout(unlock, mouseSilenceMs);
    if (locked) {
      debug("hold mouse", { dy: e.deltaY, gap: Math.round(gap) });
      return false;
    }
    locked = true;
    lockedAt = now;
    clearTimeout(releaseTimer);
    releaseTimer = setTimeout(unlock, mouseReleaseMs);
    debug("ACCEPT mouse", { dy: e.deltaY, gap: Math.round(gap) });
    return true;
  };

  const reset = () => {
    clearTimers();
    locked = false;
    burstTrackpad = false;
    history = [];
    lastEventAt = 0;
    lastAbs = 0;
    lastSign = 0;
    prevBurstLastAbs = 0;
    prevBurstLastV = 0;
    lastV = 0;
    prevBurstSign = 0;
    prevBurstEndedAt = 0;
    resumeSuspect = false;
  };

  return { accept, reset };
}
